/**
 * Полифиллы для старых WebView (Android 11 ставит Chrome WebView 90).
 *
 * Внутри приложения «О проекте» — это WebView с нашей же страницей
 * /how-it-works. На Chrome 90 react-markdown@10 звал `Object.hasOwn`
 * (появился в Chrome 93) и страница падала белым экраном: 21 событие в Sentry
 * (JAVASCRIPT-REACT-2, 17.09.2026). Vite собирает под evergreen-браузеры, без
 * @vitejs/plugin-legacy, поэтому такие методы не полифиллятся сами.
 *
 * Импортируется ПЕРВОЙ строкой main.tsx — до React и до остального кода.
 * Реализации эквивалентны спецификации; в современных браузерах файл ничего
 * не делает (все ветки — под проверкой существования).
 */

// Object.hasOwn — Chrome 93 / Safari 15.4
if (typeof (Object as any).hasOwn !== 'function') {
  Object.defineProperty(Object, 'hasOwn', {
    value: (obj: unknown, prop: PropertyKey) =>
      Object.prototype.hasOwnProperty.call(Object(obj), prop),
    configurable: true,
    writable: true,
  })
}

// structuredClone — Chrome 98 / Safari 15.4. Полная семантика (циклы, Map/Set,
// Blob) тут не нужна: в бандле он используется для простых JSON-совместимых
// структур, поэтому безопасный фолбэк — глубокое копирование через JSON.
if (typeof (globalThis as any).structuredClone !== 'function') {
  ;(globalThis as any).structuredClone = <T,>(value: T): T => {
    if (value === null || typeof value !== 'object') return value
    try {
      return JSON.parse(JSON.stringify(value)) as T
    } catch {
      return value
    }
  }
}

// Array.prototype.at / String.prototype.at — Chrome 92
if (typeof (Array.prototype as any).at !== 'function') {
  Object.defineProperty(Array.prototype, 'at', {
    value: function (this: unknown[], n: number) {
      const i = Math.trunc(n) || 0
      return this[i < 0 ? this.length + i : i]
    },
    configurable: true,
    writable: true,
  })
}
if (typeof (String.prototype as any).at !== 'function') {
  Object.defineProperty(String.prototype, 'at', {
    value: function (this: string, n: number) {
      const i = Math.trunc(n) || 0
      return this[i < 0 ? this.length + i : i]
    },
    configurable: true,
    writable: true,
  })
}

// Array.prototype.findLast / findLastIndex — Chrome 97 / Safari 15.4.
// @tiptap/core 3.32 зовёт findLast в Editor.dispatchTransaction → страница текста
// «Чтения» (/reading/<id>, редактор статей) падала белым экраном в WebView
// приложения на Android 11 (Sentry 158699366, обращение #297, 04.11.2026).
type LastPredicate = (value: unknown, index: number, array: unknown[]) => unknown
if (typeof (Array.prototype as any).findLast !== 'function') {
  Object.defineProperty(Array.prototype, 'findLast', {
    value: function (this: unknown[], predicate: LastPredicate, thisArg?: unknown) {
      if (typeof predicate !== 'function') throw new TypeError(`${String(predicate)} is not a function`)
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate.call(thisArg, this[i], i, this)) return this[i]
      }
      return undefined
    },
    configurable: true,
    writable: true,
  })
}
if (typeof (Array.prototype as any).findLastIndex !== 'function') {
  Object.defineProperty(Array.prototype, 'findLastIndex', {
    value: function (this: unknown[], predicate: LastPredicate, thisArg?: unknown) {
      if (typeof predicate !== 'function') throw new TypeError(`${String(predicate)} is not a function`)
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate.call(thisArg, this[i], i, this)) return i
      }
      return -1
    },
    configurable: true,
    writable: true,
  })
}
